import { AuthService } from '@core/auth.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import {
  HeaderOptions,
  moduleOptions,
  userRoles,
} from '@shared/config/user-roles.config';
import { Router } from '@angular/router';
import { UserResponse } from '@interfaces/hiring-manager.model';
import { VendorService } from '@services/vendor.service';
import { VendorDetails } from '@interfaces/vendor.model';
import { NotificationService } from '@services/notification.service';
import {
  NotificationTitlesEnum,
  NotificationDetails,
  RoutesEnum,
  Roles,
} from '@constants/global.enum';
import { HeaderConstants } from '@constants/constants';
import { OverlayPanel } from 'primeng/overlaypanel';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit {
  @ViewChild('notificationPanel') notificationPanel!: OverlayPanel;
  
  openMenu: boolean = false;
  isPanelOpen = false;
  isHelpPanelOpen = false;
  unreadCount = 1000;
  notifications :any[] = [];

  helpItems = HeaderConstants.help.defaultHelpItems;

  navigations: HeaderOptions[] = [];
  moduleOptions: HeaderOptions[] = [];
  currentUrl: string = '';
  jobTitle: string = '';
  vendorDetails: VendorDetails[] = [];
  currentVendor: VendorDetails | null | undefined = null;
  isVendorManagementRoute: boolean = false;
  brandText = HeaderConstants.brandText;
  headerConstants = HeaderConstants;

  constructor(
    public authService: AuthService,
    public router: Router,
    private vendorService: VendorService,
    private notificationService: NotificationService,
  ) {}

  get shouldShowNotifications() {
    const isReporties = this.authService.userProfile?.isReporties;
    return isReporties;
  }

  ngOnInit() {
    this.assignNavigations();
    this.getEmployeeJobTitle();
    this.getVendorDetails();
    this.loadNotifications();
    this.router.events.subscribe(() => {
      this.currentUrl = this.router.url;
      this.isVendorManagementRoute = Object.values(RoutesEnum).some((route) =>
        this.router.url.includes(route),
      );
    });
    this.notificationService.notifications.subscribe((notification: any) => {
      this.notifications.push(notification);
      this.unreadCount++;
    });
    
    this.notificationService.refreshNotifications$.subscribe(() => {
      this.loadNotifications();
    });
  }
  toggleNotifications(): void {
    this.isPanelOpen = !this.isPanelOpen;
    if (this.isPanelOpen && this.isHelpPanelOpen) {
      this.isHelpPanelOpen = false;
    }
  }

  closeNotifications(): void {
    this.isPanelOpen = false;
  }

  toggleHelp(): void {
    this.isHelpPanelOpen = !this.isHelpPanelOpen;
    if (this.isHelpPanelOpen && this.isPanelOpen) {
      this.isPanelOpen = false;
    }
  }

  closeHelp(): void {
    this.isHelpPanelOpen = false;
  }

  loadNotifications(): void {
    if (!this.shouldShowNotifications) {
      return;
    }
    
    const employeeId = this.authService.userProfile?.employeeId;
    if (employeeId) {
      this.notificationService.getEmployeesWithoutFeedback(employeeId).subscribe(
        (response) => {
          if (response?.data) {
            this.notifications = response.data.map((item: any) => ({
              id: item.id,
              name: item.name,
            }));
          
            this.unreadCount = this.notifications.length;
          } else {
            this.notifications = [];
            this.unreadCount = 0;
          }
        },
        (error) => {
          console.error('Error fetching notifications:', error);
        }
      );
    }
  }

  handleAction(notification: any): void {
    if (notification.action === 'View') {
      alert(`Viewing details for ${notification.title}`);
    } else if (notification.action === 'Notify') {
      alert(`Notifying about ${notification.title}`);
    }
    this.unreadCount = Math.max(this.unreadCount - 1, 0);
  }

  handleHelpAction(helpItem: any): void {
    if (
      helpItem.action === HeaderConstants.help.contactSupportText &&
      helpItem.contact
    ) {
      window.location.href = `mailto:${helpItem.contact}`;
    }
  }

  handleNotificationAction(notificationId: string): void {
    if (notificationId) {
      this.notificationPanel.hide();
      this.closeNotifications();
      this.router.navigate([`/vendor-management/contractor-profile`, notificationId], {
        queryParams: { source: 'notifications' }
      });
    }
  }

  navigateToVendorManagement(): void {
    this.router.navigate([RoutesEnum.VendorManagement]);
  }
  navigateToLogin(): void {
    this.router.navigate([`/${RoutesEnum.Login}`]);
  }
  assignNavigations() {
    if (!!this.authService.oktaUserProfile && !!this.authService.userProfile) {
      const userType =
        this.authService.getEmployeeType() as keyof typeof userRoles;
      this.navigations = userRoles[userType].headerOptions;

      if (
        Object.values(RoutesEnum).some((route) =>
          this.router.url.includes(route),
        )
      ) {
        this.navigations = [];
        return;
      }

      let userOptions: HeaderOptions[] = [];
      if (userType === Roles.IOP) {
        userOptions = moduleOptions;
      } else if (userType === Roles.OPS) {
        userOptions = moduleOptions.filter(
          (option) =>
            option.title === 'Hiring Manager' || option.title === 'OPS',
        );
      } else {
        userOptions = [];
      }
      this.moduleOptions = userOptions;
    }
  }

  getEmployeeJobTitle() {
    const emailId = this.authService.oktaUserProfile?.email;
    if (emailId) {
      this.authService
        .getEmployeeData(emailId)
        .subscribe((res: UserResponse) => {
          this.jobTitle = res?.data?.jobTitle || '';
        });
    }
  }

  getVendorDetails() {
    const emailId = this.authService.oktaUserProfile?.email?.toLowerCase();
    this.vendorService.vendorDetails$.subscribe((details: VendorDetails[]) => {
      this.vendorDetails = details;
      this.currentVendor = emailId
        ? details.find((vendor) => vendor.email.includes(emailId))
        : null;
      if (this.currentVendor) {
        this.navigations = this.navigations.map((option) => {
          if (option.path === '/vendor' && this.currentVendor?.name) {
            option.title = this.currentVendor?.name;
          }
          return option;
        });
      }
      if (this.isVendorManagementRoute) {
        this.navigations = [];
        return;
      }
    });
  }

  copyToClipboard(text: string | undefined): void {
    if (!text || text === 'Not available') {
      return;
    }

    navigator.clipboard
      .writeText(text)
      .then(() => {
        // You can add a toast notification here if you have a notification service
       
      })
      .catch((err) => {
        console.error('Failed to copy email: ', err);
        // Fallback for older browsers
        this.fallbackCopyTextToClipboard(text);
      });
  }

  private fallbackCopyTextToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        console.log('Fallback: Email copied to clipboard');
      }
    } catch (err) {
      console.error('Fallback: Unable to copy', err);
    }

    textArea.remove();
  }
}
